import { Button, Drawer, EmptyState, ErrorState, Skeleton, useToast } from "@helmr/ui";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useBlocker } from "react-router-dom";
import { isApiError } from "../../api/client";
import { NoAccess } from "../../app/NoAccess";
import { useAppContext, useSessionSnapshot, type Context } from "../../app/permissions";
import { useServices } from "../../app/services";
import s from "./settings.module.css";
import { t } from "./strings";

/** Everything a settings call needs. Every query key starts with business and branch, so a cached list never crosses a tenant. */
export function useScope() {
  const { client } = useServices();
  const ctx = useAppContext();
  const sess = useSessionSnapshot();
  const qc = useQueryClient();
  return { client, ctx, qc, key: (...parts: unknown[]) => ["settings", sess.businessId, sess.branchId, ...parts] as const, businessId: sess.businessId };
}

/** Page chrome plus the permission gate. Hiding is cosmetic: the server still enforces. */
export function Page({ title, description, allow, back = true, actions, children }: {
  title: string; description?: string; allow: (has: Context["has"]) => boolean; back?: boolean; actions?: ReactNode; children: ReactNode;
}) {
  const ctx = useAppContext();
  if (ctx.status !== "loading" && !allow(ctx.has)) return <NoAccess />;
  return (
    <div className={s.page}>
      <div className={s.head}>
        <div>
          {back ? <Link to="/settings" className={s.crumb}>{t.back}</Link> : null}
          <h1 tabIndex={-1}>{title}</h1>
          {description ? <p>{description}</p> : null}
        </div>
        {actions ? <div className={s.acts}>{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

export function FormSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className={s.skel} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <div key={i} style={{ display: "grid", gap: 6 }}><Skeleton width={120} height={12} /><Skeleton height={34} /></div>)}
    </div>
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className={s.skel} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={34} />)}
    </div>
  );
}

/** One query, one fate: skeleton, then 404 = "Not connected yet", 403 = no access, anything else = message + Retry + request id. */
export function Load<T>({ query, skeleton, what, children }: {
  query: Pick<UseQueryResult<T>, "data" | "error" | "isPending" | "refetch">; skeleton?: ReactNode; what: string; children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{skeleton ?? <FormSkeleton />}</>;
  if (query.error) return <Failure error={query.error} what={what} onRetry={() => void query.refetch()} />;
  return <>{children(query.data as T)}</>;
}

export function Failure({ error, what, onRetry }: { error: unknown; what: string; onRetry: () => void }) {
  if (isApiError(error) && error.status === 404) return <EmptyState compact icon="clock" title={t.notConnected} description={t.notConnectedBody(what)} />;
  if (isApiError(error) && error.status === 403) return <EmptyState compact icon="alert" title={t.noAccess} description={t.noAccessBody} />;
  return <ErrorState compact title={`Could not load ${what}`} message={isApiError(error) ? error.message : "Something went wrong."} requestId={isApiError(error) ? error.requestId : null} onRetry={onRetry} />;
}

/* ---------- errors onto fields ---------- */

export interface Mapped { fields: Record<string, string>; form: string | null }

/**
 * The API reports validation as `400 bad_request` with one message that names the field (`taxId is not a valid GSTIN`,
 * `hours.mon intervals overlap`); it sends no `422` field list on `main`. A message is mapped onto the field whose key it names
 * (dotted paths too), longest key first; anything else stays a form-level message. A `422` is treated the same way.
 */
export function mapError(err: unknown, keys: readonly string[]): Mapped {
  if (!isApiError(err)) return { fields: {}, form: "Something went wrong. Try again." };
  if (err.isOffline) return { fields: {}, form: err.message };
  if (err.status === 409) return { fields: {}, form: err.message };
  if (err.status === 403) return { fields: {}, form: err.message || t.noAccess };
  if (err.status === 400 || err.status === 422) {
    const msg = err.message;
    // The key named earliest wins: "halfDayMinutes must not exceed fullDayMinutes" is about the first.
    const at = (k: string) => msg.search(new RegExp(`(^|[^A-Za-z0-9_])${k.replace(/\./g, "\\.")}([^A-Za-z0-9_]|$)`));
    const hit = keys.filter((k) => at(k) >= 0).sort((a, b) => at(a) - at(b) || b.length - a.length)[0];
    return hit ? { fields: { [hit]: msg }, form: null } : { fields: {}, form: msg };
  }
  return { fields: {}, form: err.message };
}

/* ---------- dirty guard ---------- */

/** Blocks in-app navigation and tab close while `dirty`, with a confirm. Render the returned node once. */
export function useDirtyGuard(dirty: boolean): { node: ReactNode; release: () => void } {
  // Read at navigation time, so a save that redirects can release the guard in the same tick.
  const live = useRef(dirty);
  live.current = dirty;
  const blocker = useBlocker(({ currentLocation, nextLocation }) => live.current && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  // Saving clears `dirty` before the redirect, so a blocked state that outlives it is released.
  useEffect(() => { if (blocker.state === "blocked" && !dirty) blocker.proceed(); }, [blocker, dirty]);
  const node = (
    <Drawer open={blocker.state === "blocked"} onOpenChange={(o) => { if (!o && blocker.state === "blocked") blocker.reset(); }} title={t.dirty.title} description={t.dirty.body}
      footer={<><Button onClick={() => blocker.state === "blocked" && blocker.reset()}>{t.dirty.stay}</Button><Button variant="primary" onClick={() => blocker.state === "blocked" && blocker.proceed()}>{t.dirty.leave}</Button></>}>
      <p>{t.dirty.body}</p>
    </Drawer>
  );
  return { node, release: () => { live.current = false; } };
}

/* ---------- confirm and secret drawers ---------- */

/** A confirm. With `typeToConfirm` the button stays disabled until that exact text is typed (branch and brand delete). */
export function ConfirmDrawer({ open, onClose, title, description, confirmLabel, busyLabel, onConfirm, busy, error, typeToConfirm, children }: {
  open: boolean; onClose: () => void; title: string; description?: string; confirmLabel: string; busyLabel?: string;
  onConfirm: () => void; busy?: boolean; error?: string | null; typeToConfirm?: string; children?: ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const id = useId();
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  const ready = typeToConfirm === undefined || typed.trim() === typeToConfirm;
  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={title} description={description}
      footer={<><Button onClick={onClose}>{t.cancel}</Button><Button variant="primary" busy={busy} busyLabel={busyLabel} disabled={!ready} onClick={onConfirm}>{confirmLabel}</Button></>}>
      <div className={s.stack}>
        {children}
        {typeToConfirm !== undefined ? (
          <div className={s.field}>
            <label htmlFor={id}>Type <b>{typeToConfirm}</b> to confirm</label>
            <input id={id} className={s.input} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </div>
        ) : null}
        {error ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{error}</div> : null}
      </div>
    </Drawer>
  );
}

/**
 * A secret that is shown once. It lives in this component's props for as long as the drawer is open and nowhere else:
 * not the query cache, not localStorage, not a log. Closing drops it (the parent clears its state in `onDone`).
 */
export function SecretDrawer({ open, title, description, label, secret, extra, onDone }: {
  open: boolean; title: string; description: string; label: string; secret: string | null; extra?: ReactNode; onDone: () => void;
}) {
  const toast = useToast();
  async function copy() {
    if (!secret) return;
    try { await navigator.clipboard.writeText(secret); toast.show("Copied"); } catch { toast.show("Could not copy: select the text and copy it"); }
  }
  // Escape and the close button both mean "I've saved it" only if they confirm; an accidental close would lose the secret for good.
  return (
    <Drawer open={open && secret !== null} onOpenChange={() => { /* closed only through the confirm button */ }} title={title} description={description}
      footer={<><Button onClick={() => void copy()}>Copy</Button><Button variant="primary" onClick={onDone}>I&apos;ve saved it</Button></>}>
      <div className={s.stack}>
        <div className={s.field}><span className={s.lab}>{label}</span><code className={s.secret} data-testid="secret">{secret}</code></div>
        <p className={s.hint}>This is the only time it is shown. Helmr keeps only a fingerprint and cannot show it again.</p>
        {extra}
      </div>
    </Drawer>
  );
}

/* ---------- fields ---------- */

export function Field({ label, hint, error, full, children, htmlFor }: { label: string; hint?: string; error?: string; full?: boolean; children: ReactNode; htmlFor?: string }) {
  return (
    <div className={`${s.field}${full ? ` ${s.full}` : ""}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <span className={s.err} role="alert">{error}</span> : hint ? <span className={s.hint}>{hint}</span> : null}
    </div>
  );
}

export function SavedBar({ dirty, busy, onDiscard, canEdit, children, alwaysSave }: { alwaysSave?: boolean; dirty: boolean; busy: boolean; onDiscard: () => void; canEdit: boolean; children?: ReactNode }) {
  return (
    <div className={s.bar}>
      <span className={s.hint}>{!canEdit ? "You can view this, not change it." : dirty ? "Unsaved changes" : "Up to date"}</span>
      <div className={s.acts}>
        {children}
        <Button disabled={!dirty || busy} onClick={onDiscard}>{t.discard}</Button>
        <Button variant="primary" type="submit" busy={busy} busyLabel={t.saving} disabled={(!dirty && !alwaysSave) || !canEdit}>{t.save}</Button>
      </div>
    </div>
  );
}

export const fmtDateTime = (ms: number, timeZone: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(ms);


const parseCsv = (raw: string) => raw.split(",").map((x) => x.trim()).filter(Boolean);

/** A comma-separated list in one text box. It keeps what is typed (a trailing comma must survive the next keystroke) and reports the parsed list. */
export function CsvInput({ value, onChange, ...rest }: { value: string[]; onChange: (v: string[]) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  const [text, setText] = useState(value.join(", "));
  // Follow the outside only when it is genuinely different from what the text already means (a Discard, a save).
  useEffect(() => { if (JSON.stringify(parseCsv(text)) !== JSON.stringify(value)) setText(value.join(", ")); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <input className={s.input} {...rest} value={text} onChange={(e) => { setText(e.target.value); onChange(parseCsv(e.target.value)); }} />;
}

/**
 * Wraps a route: until `/me` says the role may open it, nothing inside renders, so no request for data the role cannot see is made.
 * Hiding is cosmetic; the server still enforces.
 */
export function Guarded({ allow, children }: { allow: (has: Context["has"]) => boolean; children: ReactNode }) {
  const ctx = useAppContext();
  if (ctx.status === "loading") return <FormSkeleton rows={3} />;
  if (!allow(ctx.has)) return <NoAccess />;
  return <>{children}</>;
}
